import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { getRooms, updateOccupancy, recalculatePrices } from "../lib/api";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw, 
  Save, 
  Users, 
  Bed,
  TrendingUp,
  Calculator
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickOccupancy = () => {
  const [rooms, setRooms] = useState([]);
  const [occupancyValues, setOccupancyValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchRooms = async () => {
    try {
      const response = await getRooms();
      setRooms(response.data);
      // Initialize occupancy values
      const initial = {};
      response.data.forEach(room => {
        initial[room.id] = room.occupied_rooms;
      });
      setOccupancyValues(initial);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      toast.error("Error al cargar habitaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleOccupancyChange = (roomId, value) => {
    const room = rooms.find(r => r.id === roomId);
    const numValue = parseInt(value) || 0;
    const maxValue = room?.total_rooms || 0;
    
    setOccupancyValues(prev => ({
      ...prev,
      [roomId]: Math.min(Math.max(0, numValue), maxValue)
    }));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const updates = rooms.map(room => ({
        room_type_id: room.id,
        occupied_rooms: occupancyValues[room.id]
      }));
      
      await axios.put(`${API}/rooms/bulk-occupancy`, { updates });
      toast.success("Ocupación actualizada y precios recalculados");
      setHasChanges(false);
      fetchRooms();
    } catch (error) {
      toast.error("Error al guardar ocupación");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      await recalculatePrices();
      toast.success("Precios recalculados");
      fetchRooms();
    } catch (error) {
      toast.error("Error al recalcular precios");
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const totalRooms = rooms.reduce((sum, r) => sum + r.total_rooms, 0);
  const totalOccupied = Object.values(occupancyValues).reduce((sum, v) => sum + v, 0);
  const overallOccupancy = totalRooms > 0 ? ((totalOccupied / totalRooms) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quick-occupancy-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Actualización Rápida</h1>
          <p className="text-muted-foreground">Actualiza la ocupación de todas las habitaciones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRecalculate} data-testid="recalculate-btn">
            <Calculator className="w-4 h-4 mr-2" />
            Recalcular Precios
          </Button>
          <Button 
            onClick={handleSaveAll} 
            disabled={saving || !hasChanges}
            data-testid="save-all-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar Todo
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold">{totalRooms}</p>
              <p className="text-sm text-muted-foreground">Total Habitaciones</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{totalOccupied}</p>
              <p className="text-sm text-muted-foreground">Ocupadas</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{overallOccupancy}%</p>
              <p className="text-sm text-muted-foreground">Ocupación Total</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map((room) => {
          const currentOccupancy = occupancyValues[room.id] || 0;
          const occupancyRate = room.total_rooms > 0 
            ? ((currentOccupancy / room.total_rooms) * 100).toFixed(0)
            : 0;
          const isChanged = currentOccupancy !== room.occupied_rooms;
          const priceChange = room.current_price - room.base_price;
          
          return (
            <Card 
              key={room.id} 
              className={`transition-all ${isChanged ? "ring-2 ring-primary" : ""}`}
              data-testid={`room-occupancy-${room.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{room.name}</CardTitle>
                    <CardDescription className="text-xs">
                      {room.oscar_code && <Badge variant="outline" className="mr-2">{room.oscar_code}</Badge>}
                      {room.total_rooms} habitaciones
                    </CardDescription>
                  </div>
                  <Badge variant={
                    occupancyRate >= 80 ? "destructive" :
                    occupancyRate >= 50 ? "default" :
                    "secondary"
                  }>
                    {occupancyRate}%
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">Ocupadas</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={room.total_rooms}
                        value={currentOccupancy}
                        onChange={(e) => handleOccupancyChange(room.id, e.target.value)}
                        className="w-20 text-center font-mono"
                        data-testid={`occupancy-input-${room.id}`}
                      />
                      <span className="text-muted-foreground">/ {room.total_rooms}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <Label className="text-xs text-muted-foreground">Precio</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-bold">€{room.current_price}</span>
                      {priceChange !== 0 && (
                        <span className={`text-xs ${priceChange > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {priceChange > 0 ? "+" : ""}{((priceChange / room.base_price) * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Quick buttons */}
                <div className="flex gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleOccupancyChange(room.id, 0)}
                  >
                    Vacío
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleOccupancyChange(room.id, Math.floor(room.total_rooms / 2))}
                  >
                    50%
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleOccupancyChange(room.id, room.total_rooms)}
                  >
                    Lleno
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Ajuste Automático de Precios</p>
              <p className="text-sm text-muted-foreground">
                Al guardar los cambios, el sistema recalculará automáticamente los precios según las reglas configuradas:
                ocupación &gt;80% (+15%), ocupación &gt;95% (+25%), ocupación &lt;30% (-10%).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickOccupancy;
